import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  getAgencyAgents,
  getAgencyPlayers,
  addManualEntry,
  searchService,
} from "../../services/api";
import { ROLES } from "../../config/roles";
import CustomDateTimePicker from "../../components/common/CustomDateTimePicker";

const ChevronLeftIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 19l-7-7 7-7"
    />
  </svg>
);
const PencilIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
    />
  </svg>
);
const CalendarIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
  </svg>
);
const ChevronDownIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 9l-7 7-7-7"
    />
  </svg>
);

const statusOptions = [
  { value: "waiting", label: "Waiting", color: "bg-orange-400" },
  { value: "accepted", label: "Accepted", color: "bg-cyan-400" },
  { value: "rejected", label: "Rejected", color: "bg-red-400" },
];
const deviceOptions = [
  { value: "Email", label: "Email" },
  { value: "Phone", label: "Phone" },
  { value: "WhatsApp", label: "WhatsApp" },
  { value: "Meeting", label: "Meeting" },
];

const AddManualEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Backend behaviour: agency admins choose which agent the entry belongs to;
  // for non-admin agents the backend always files the entry under the caller
  // and ignores any agentId in the payload. Hide the dropdown for non-admins
  // so they don't see a control that's effectively dead.
  const { currentRole } = useSelector((state) => state.auth);
  const isCurrentUserAdmin = currentRole === ROLES.AgencyAdmin;
  const [agents, setAgents] = useState([]);
  const [players, setPlayers] = useState([]);
  const [agentsLoading, setAgentsLoading] = useState(false);
  const [playersLoading, setPlayersLoading] = useState(false);
  const [agentSearch, setAgentSearch] = useState("");
  const [playerSearch, setPlayerSearch] = useState("");
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [showPlayerDropdown, setShowPlayerDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
        
  const [formData, setFormData] = useState({
    player: null,
    agent: null,
    club: null,
    status: null,
    deviceUsed: null,
    date: "",
    notes: "",
  });

  // Dummy clubs for fallback
  const clubs = [
    { id: 1, name: "Chelsea Football Club", logo: "/public/logo512.png" },
    { id: 2, name: "Real Madrid", logo: "/public/logo512.png" },
    { id: 3, name: "Manchester United", logo: "/public/logo512.png" },
  ];
  const [showClubModal, setShowClubModal] = useState(false);
  const [clubSearch, setClubSearch] = useState("");
  const [clubSearchResults, setClubSearchResults] = useState([]);
  const [clubSearchLoading, setClubSearchLoading] = useState(false);
  const [clubSearchPagination, setClubSearchPagination] = useState(null);
  const [clubCurrentPage, setClubCurrentPage] = useState(1);

  // Fetch agents and players
  useEffect(() => {
    setAgentsLoading(true);
    getAgencyAgents({ type: "licensed" })
      .then((res) => {
        const data = res.data?.results?.data?.licensed || [];
        setAgents(data);
      })
      .catch(() => setAgents([]))
      .finally(() => setAgentsLoading(false));
    setPlayersLoading(true);
    getAgencyPlayers({ page: 1, limit: 100 })
      .then((res) => {
        setPlayers(Array.isArray(res.data?.results) ? res.data.results : []);
      })
      .catch(() => setPlayers([]))
      .finally(() => setPlayersLoading(false));
  }, []);

  // If player is passed from navigation state, pre-select and disable
  useEffect(() => {
    if (location.state && location.state.player) {
      setFormData((prev) => ({ ...prev, player: location.state.player }));
    }
  }, [location.state]);

  // Dropdown search filters
  const filteredAgents = agentSearch
    ? agents.filter((a) =>
        a.name.toLowerCase().includes(agentSearch.toLowerCase())
      )
    : agents;
  const filteredPlayers = playerSearch
    ? players.filter((p) =>
        p.name.toLowerCase().includes(playerSearch.toLowerCase())
      )
    : players;
  // Club search functionality
  const searchClubs = async (query, page = 1) => {
    if (!query.trim()) {
      setClubSearchResults([]);
      return;
    }

    setClubSearchLoading(true);
    try {
      const params = {
        type: "clubs",
        query: query.trim(),
        page: page,
        per_page: 10,
      };
      const response = await searchService.search(params);

      if (response.success) {
        // Updated to handle new response structure
        const clubs = response.data.results?.clubs || [];
        const mapped = clubs.map((club) => ({
          id: club.id,
          name: club.name || club.display_name || "",
          logo: club.logo || club.image_path || "/public/logo512.png",
        }));
        setClubSearchResults(mapped);
        setClubSearchPagination(response.pagination || null);
      } else {
        console.error("Club search failed:", response.error);
        setClubSearchResults([]);
        setClubSearchPagination(null);
      }
    } catch (error) {
      console.error("Club search error:", error);
      setClubSearchResults([]);
      setClubSearchPagination(null);
    } finally {
      setClubSearchLoading(false);
    }
  };

  const handleClubPageChange = (page) => {
    if (clubSearch.trim()) {
      setClubCurrentPage(page);
      searchClubs(clubSearch, page);
    }
  };

  // Debounced club search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (clubSearch.trim() && showClubModal) {
        searchClubs(clubSearch, 1);
        setClubCurrentPage(1);
      } else if (!clubSearch.trim()) {
        setClubSearchResults([]);
        setClubSearchPagination(null);
        setClubSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [clubSearch, showClubModal]);

  // Handlers
  const handleSelectAgent = (agent) => {
    setFormData((prev) => ({ ...prev, agent }));
    setShowAgentDropdown(false);
    setAgentSearch("");
  };
  const handleSelectPlayer = (player) => {
    setFormData((prev) => ({ ...prev, player }));
    setShowPlayerDropdown(false);
    setPlayerSearch("");
  };
  const handleSelectClub = (club) => {
    setFormData((prev) => ({ ...prev, club }));
    setShowClubModal(false);
    setClubSearch("");
    setClubSearchResults([]);
    setClubSearchPagination(null);
  };
  const handleSelectStatus = (status) => {
    setFormData((prev) => ({ ...prev, status }));
    setShowStatusDropdown(false);
  };
  const handleSelectDevice = (deviceUsed) => {
    setFormData((prev) => ({ ...prev, deviceUsed }));
    setShowDeviceDropdown(false);
  };
  const handleDateChange = (e) => {
    setFormData((prev) => ({ ...prev, date: e.target.value }));
  };
  const handleNotesChange = (e) => {
    setFormData((prev) => ({ ...prev, notes: e.target.value }));
  };
  const handleBack = () => navigate(-1);
  const handleCancel = () => navigate(-1);
  // Pencil icon clear and open dropdown
  const handleEditAgent = () => {
    setFormData((prev) => ({ ...prev, agent: null }));
    setShowAgentDropdown(true);
  };
  const handleEditPlayer = () => {
    setFormData((prev) => ({ ...prev, player: null }));
    setShowPlayerDropdown(true);
  };
  const handleEditClub = () => {
    setFormData((prev) => ({ ...prev, club: null }));
    setShowClubModal(true);
  };

  const handleOpenClubSearch = () => {
    setShowClubModal(true);
  };

  // Save handler (API integration to be added)
  const handleSave = async () => {
    setError("");
    setSuccess("");
    // Validate required fields. Agent selection is only required for admins —
    // for non-admins the backend auto-assigns the entry to the caller.
    if (
      !formData.player ||
      (isCurrentUserAdmin && !formData.agent) ||
      !formData.club ||
      !formData.status ||
      !formData.deviceUsed ||
      !formData.date
    ) {
      setError("Please fill all required fields.");
      return;
    }
    setLoading(true);
    try {
      // Format date to YYYY-MM-DD if needed
      let date = formData.date;
      if (date && date.includes("-")) {
        // already in YYYY-MM-DD
      } else if (date && date.includes(".")) {
        // convert from dd.mm.yyyy
        const [d, m, y] = date.split(".");
        date = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
      }
      console.log("formData", formData);
      const payload = {
        playerId: formData.player?.wyscout_id,
        agentId: formData.agent?.account_id,
        clubId: formData.club?.wyscout_id,
        status: formData.status,
        deviceUsed: formData.deviceUsed.toLowerCase(),
        date,
        notes: formData.notes,
      };
      await addManualEntry(payload);
      setSuccess("Entry added successfully.");
      setTimeout(() => navigate(-1), 1200);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to add entry.");
    } finally {
      setLoading(false);
    }
  };

  // Dropdown close on outside click
  const agentDropdownRef = useRef();
  const playerDropdownRef = useRef();
  useEffect(() => {
    const handleClick = (e) => {
      if (
        agentDropdownRef.current &&
        !agentDropdownRef.current.contains(e.target)
      )
        setShowAgentDropdown(false);
      if (
        playerDropdownRef.current &&
        !playerDropdownRef.current.contains(e.target)
      )
        setShowPlayerDropdown(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Render
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white  px-12 py-4">
        <div className="flex items-center">
          <button
            onClick={handleBack}
            className="mr-4 p-1 rounded-md hover:bg-gray-100 transition-colors"
            aria-label="Back"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.8327 6.99984H1.16602M1.16602 6.99984L6.99935 12.8332M1.16602 6.99984L6.99935 1.1665"
                stroke="#2C2C2C"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <h1 className="text-xl font-bold text-[#202630]">Add manual entry</h1>
        </div>
      </div>
      {/* Form Content */}
      <div className="max-w-6xl mx-auto p-8">
        {error && <div className="mb-4 text-red-500 text-sm">{error}</div>}
        {success && (
          <div className="mb-4 text-green-600 text-sm">{success}</div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Player */}
          <div>
            <label className="block text-[16px] font-medium text-black mb-2">
              Player
            </label>
            <div className="relative" ref={playerDropdownRef}>
              {formData.player ? (
                <button
                  type="button"
                  className="w-full flex items-center justify-between bg-white border border-[#E9E9E9] rounded-lg px-4 py-3 focus:outline-none hover:border-gray-300 transition-colors"
                  style={{ height: 48 }}
                  onClick={
                    location.state && location.state.player
                      ? undefined
                      : handleEditPlayer
                  }
                  aria-label="Edit player"
                  disabled={!!(location.state && location.state.player)}
                  tabIndex={location.state && location.state.player ? -1 : 0}
                >
                  <div className="flex items-center">
                    <img
                      src={
                        formData.player.image ||
                        formData.player.player_image ||
                        "/default_profile.webp"
                      }
                      alt={formData.player.name}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                    <span className="ml-3 text-[16px] font-medium text-[#242426]">
                      {formData.player.name}
                    </span>
                  </div>
                  {/* Only show pencil if not disabled */}
                  {!(location.state && location.state.player) && (
                    <PencilIcon className="h-4 w-4 text-[#626262]" />
                  )}
                </button>
              ) : (
                <button
                  onClick={() => setShowPlayerDropdown((v) => !v)}
                  className="w-full bg-white border border-[#E9E9E9] rounded-lg px-4 py-3 text-left flex items-center justify-between hover:border-gray-300"
                  aria-haspopup="listbox"
                  aria-expanded={showPlayerDropdown}
                  style={{ height: 48 }}
                >
                  <span className="text-[#00000099] font-medium text-[16px]">
                    Select Player
                  </span>
                  <ChevronDownIcon className="h-4 w-4 text-[#626262]" />
                </button>
              )}
              {/* Dropdown only if not disabled */}
              {showPlayerDropdown &&
                !formData.player &&
                !(location.state && location.state.player) && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                    <input
                      type="text"
                      className="w-full px-4 py-2 border-b border-gray-100 text-sm focus:outline-none placeholder:text-[#00000099] placeholder:font-medium placeholder:text-[16px]"
                      placeholder="Search player..."
                      value={playerSearch}
                      onChange={(e) => setPlayerSearch(e.target.value)}
                      autoFocus
                    />
                    {playersLoading ? (
                      <div className="p-4 text-center text-gray-400 text-sm">
                        Loading…
                      </div>
                    ) : filteredPlayers.length === 0 ? (
                      <div className="p-4 text-center text-gray-400 text-sm">
                        No players found
                      </div>
                    ) : (
                      filteredPlayers.map((player) => (
                        <button
                          key={player.id}
                          onClick={() => handleSelectPlayer(player)}
                          className="w-full flex items-center px-4 py-3 hover:bg-gray-50 text-sm"
                        >
                          <img
                            src={
                              player.image ||
                              player.player_image ||
                              "/default_profile.webp"
                            }
                            alt={player.name}
                            className="h-8 w-8 rounded-full object-cover mr-3"
                          />
                          <span>{player.name}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
            </div>
          </div>
          {/* Agent — admins only. For non-admin agents the backend always
              files the entry under the caller, so the dropdown is hidden to
              avoid showing a control that has no effect. */}
          {isCurrentUserAdmin && (
          <div>
            <label className="block text-[16px] font-medium text-black mb-2">
              Agent
            </label>
            <div className="relative" ref={agentDropdownRef}>
              {formData.agent ? (
                <button
                  type="button"
                  className="w-full flex items-center justify-between bg-white border border-[#E9E9E9] rounded-lg px-4 py-3 focus:outline-none hover:border-gray-300 transition-colors"
                  style={{ height: 48 }}
                  onClick={handleEditAgent}
                  aria-label="Edit agent"
                >
                  <div className="flex items-center">
                    <img
                      src={formData.agent.image || "/default_profile.webp"}
                      alt={formData.agent.name}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                    <span className="ml-3 text-[16px] font-medium text-[#242426]">
                      {formData.agent.name}
                    </span>
                  </div>
                  <PencilIcon className="h-4 w-4 text-[#626262]" />
                </button>
              ) : (
                <button
                  onClick={() => setShowAgentDropdown((v) => !v)}
                  className="w-full bg-white border border-[#E9E9E9] rounded-lg px-4 py-3 text-left flex items-center justify-between hover:border-gray-300"
                  aria-haspopup="listbox"
                  aria-expanded={showAgentDropdown}
                  style={{ height: 48 }}
                >
                  <span className="text-[16px] text-[#00000099] font-medium">
                    Select agent
                  </span>
                  <ChevronDownIcon className="h-4 w-4 text-[#626262]" />
                </button>
              )}
              {showAgentDropdown && !formData.agent && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                  <input
                    type="text"
                    className="w-full px-4 py-2 border-b border-gray-100 text-sm focus:outline-none placeholder:text-[#00000099] placeholder:font-medium placeholder:text-[16px]"
                    placeholder="Search agent..."
                    value={agentSearch}
                    onChange={(e) => setAgentSearch(e.target.value)}
                    autoFocus
                  />
                  {agentsLoading ? (
                    <div className="p-4 text-center text-gray-400 text-sm">
                      Loading…
                    </div>
                  ) : filteredAgents.length === 0 ? (
                    <div className="p-4 text-center text-gray-400 text-sm">
                      No agents found
                    </div>
                  ) : (
                    filteredAgents.map((agent) => (
                      <button
                        key={agent.id}
                        onClick={() => handleSelectAgent(agent)}
                        className="w-full flex items-center px-4 py-3 hover:bg-gray-50 text-sm"
                      >
                        <img
                          src={agent.image || "/default_profile.webp"}
                          alt={agent.name}
                          className="h-8 w-8 rounded-full object-cover mr-3"
                        />
                        <span>{agent.name}</span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          )}
          {/* Club */}
          <div>
            <label className="block text-[16px] font-medium text-black mb-2">
              Pitched to Club
            </label>
            <div className="relative">
              {formData.club ? (
                <button
                  type="button"
                  className="w-full flex items-center justify-between bg-white border border-[#E9E9E9] rounded-lg px-4 py-3 focus:outline-none hover:border-gray-300 transition-colors"
                  style={{ height: 48 }}
                  onClick={handleEditClub}
                  aria-label="Edit club"
                >
                  <div className="flex items-center">
                    <img
                      src={formData.club.logo}
                      alt={formData.club.name}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                    <span className="ml-3 text-[16px] font-medium text-[#242426]">
                      {formData.club.name}
                    </span>
                  </div>
                  <PencilIcon className="h-4 w-4 text-[#626262]" />
                </button>
              ) : (
                <button
                  onClick={handleOpenClubSearch}
                  className="w-full bg-white border border-[#E9E9E9] rounded-lg px-4 py-3 text-left flex items-center justify-between hover:border-gray-300"
                  aria-haspopup="listbox"
                  style={{ height: 48 }}
                >
                  <span className="text-[16px] text-[#00000099] font-medium">
                    Select Club
                  </span>
                  <ChevronDownIcon className="h-4 w-4 text-[#626262]" />
                </button>
              )}
            </div>
          </div>
          {/* Status */}
          <div>
            <label className="block text-[16px] font-medium text-black mb-2">
              Status
            </label>
            <div className="relative">
              <button
                onClick={() => setShowStatusDropdown((v) => !v)}
                className="w-full bg-white border border-[#E9E9E9] rounded-lg px-4 py-3 text-left flex items-center justify-between hover:border-gray-300"
                aria-haspopup="listbox"
                aria-expanded={showStatusDropdown}
                style={{ minHeight: 48 }}
              >
                <div className="flex items-center">
                  {formData.status ? (
                    <span
                      className={`w-2 h-2 rounded-full ${
                        statusOptions.find((s) => s.value === formData.status)
                          ?.color
                      } mr-3`}
                    ></span>
                  ) : null}
                  <span
                    className={`text-[16px] font-medium ${
                      formData.status ? "text-[#242426]" : "text-[#00000099]"
                    }`}
                  >
                    {formData.status
                      ? statusOptions.find((s) => s.value === formData.status)
                          ?.label
                      : "Status"}
                  </span>
                </div>
                <ChevronDownIcon className="h-4 w-4 text-[#626262]" />
              </button>
              {showStatusDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleSelectStatus(option.value)}
                      className="w-full flex items-center px-4 py-3 hover:bg-gray-50 text-sm first:rounded-t-lg last:rounded-b-lg"
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${option.color} mr-3`}
                      ></span>
                      <span>{option.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* Device Used */}
          <div>
            <label className="block text-[16px] font-medium text-black mb-2">
              Device used
            </label>
            <div className="relative">
              <button
                onClick={() => setShowDeviceDropdown((v) => !v)}
                className="w-full bg-white border border-[#E9E9E9] rounded-lg px-4 py-3 text-left flex items-center justify-between hover:border-gray-300"
                aria-haspopup="listbox"
                aria-expanded={showDeviceDropdown}
                style={{ minHeight: 48 }}
              >
                <span
                  className={`text-[16px] font-medium ${
                    formData.deviceUsed ? "text-[#242426]" : "text-[#00000099]"
                  }`}
                >
                  {formData.deviceUsed || "Device"}
                </span>
                <ChevronDownIcon className="h-4 w-4 text-[#626262]" />
              </button>
              {showDeviceDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                  {deviceOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleSelectDevice(option.value)}
                      className="w-full text-left px-4 py-3 hover:bg-gray-50 text-sm first:rounded-t-lg last:rounded-b-lg"
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          {/* Date */}
          <div>
            <CustomDateTimePicker
              label="Date"
              value={formData.date ? new Date(formData.date) : null}
              onChange={(date) => {
                const formattedDate = date
                  ? date.toISOString().split("T")[0]
                  : "";
                setFormData((prev) => ({ ...prev, date: formattedDate }));
              }}
              placeholder="Select date"
              required
              className="w-full bg-white border border-[#E9E9E9] rounded-lg px-4 py-3 text-[16px] font-medium text-[#242426] hover:border-gray-300 transition-colors"
            />
          </div>
        </div>

        {/* Club Search Modal */}
        {showClubModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowClubModal(false)}
          >
            <div
              className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-semibold text-[#202630]">
                  Search Clubs
                </h2>
                <button
                  type="button"
                  onClick={() => setShowClubModal(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Search Input */}
              <div className="p-6 border-b">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search for a club..."
                    value={clubSearch}
                    onChange={(e) => setClubSearch(e.target.value)}
                    className="w-full h-12 pl-12 pr-4 border border-[#E9E9E9] rounded-[5px] text-base placeholder-[#626262] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                  <svg
                    className="w-5 h-5 text-[#626262] absolute left-4 top-1/2 -translate-y-1/2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                  {clubSearchLoading && (
                    <div className="absolute right-4 top-1/2 -translate-y-1/2">
                      <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                    </div>
                  )}
                </div>
              </div>

              {/* Search Results */}
              <div className="flex-1 overflow-y-auto p-6">
                {clubSearchLoading && clubSearchResults.length === 0 ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                      <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                      <p className="text-secondary">Searching clubs...</p>
                    </div>
                  </div>
                ) : clubSearchResults.length > 0 ? (
                  <div className="space-y-4">
                    {/* Results List */}
                    <div className="space-y-2">
                      {clubSearchResults.map((club) => (
                        <button
                          key={club.id}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSelectClub(club);
                          }}
                          className="w-full p-4 border border-[#E9E9E9] rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
                        >
                          <div className="flex items-center gap-4">
                            <div
                              className="w-12 h-12 rounded-full bg-gray-300 bg-cover bg-center flex-shrink-0"
                              style={{
                                backgroundImage:
                                  club.logo &&
                                  !club.logo.includes("placeholder")
                                    ? `url(${club.logo})`
                                    : "none",
                              }}
                            />

                            <div className="flex-1 min-w-0">
                              <h3 className="text-base font-medium text-[#1A212F] truncate">
                                {club.name}
                              </h3>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>

                    {/* Pagination */}
                    {clubSearchPagination && (
                      <div className="pt-4 border-t">
                        <div className="flex items-center justify-between">
                          <div className="text-sm text-secondary"></div>

                          {(clubSearchPagination.total_pages > 1 ||
                            clubSearchPagination.has_more) && (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClubPageChange(clubCurrentPage - 1);
                                }}
                                disabled={clubCurrentPage <= 1}
                                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                              >
                                Previous
                              </button>

                              <span className="px-2 text-sm text-secondary">
                                {clubCurrentPage}
                              </span>

                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleClubPageChange(clubCurrentPage + 1);
                                }}
                                disabled={
                                  !clubSearchPagination.has_more &&
                                  clubCurrentPage >=
                                    clubSearchPagination.total_pages
                                }
                                className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                              >
                                Next
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : clubSearch.trim() && !clubSearchLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                      <svg
                        className="w-12 h-12 text-gray-300 mx-auto mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      <p className="text-secondary">
                        No clubs found for "{clubSearch}"
                      </p>
                      <p className="text-sm text-gray-400 mt-1">
                        Try adjusting your search terms
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                      <svg
                        className="w-12 h-12 text-gray-300 mx-auto mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                      <p className="text-secondary">
                        Start typing to search for clubs
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Notes Section */}
        <div className="mt-8">
          <label className="block text-[16px] font-medium text-black mb-2">
            Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={handleNotesChange}
            rows={4}
            className="w-full bg-white border border-[#E9E9E9] rounded-lg p-4 text-sm text-[#242426] resize-none hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Add notes"
            aria-label="Notes"
          />
        </div>
        {/* Action Buttons */}
        <div className="mt-8 flex items-center space-x-4">
          <button
            onClick={handleSave}
            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-60"
            disabled={loading}
          >
            {loading ? "Saving..." : "Save entry"}
          </button>
          <button
            onClick={handleCancel}
            className="text-[#00000099] px-6 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddManualEntry;
